import { faCircleInfo, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from './Button.tsx';
import { withIcon } from './Icon.tsx';
import classnames from '../utils/classnames.tsx';

const InfoIcon = withIcon(faCircleInfo);
const SpinnerIcon = withIcon(faSpinnerThird);

interface SaveStatusProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onClick'> {
    isDirty: boolean;
    isSubmitting?: boolean;
    errorCount?: number;
    isDisabled?: boolean;
    onSave?: () => void;
    children?: React.ReactNode;
}

const Dot = ({ className }: { className: string }) => (
    <span aria-hidden className={classnames('inline-block h-2.5 w-2.5 flex-none rounded-full', className)} />
);

const useJustSaved = (isDirty: boolean, isSubmitting: boolean, errorCount: number) => {
    const [justSaved, setJustSaved] = useState(false);
    const wasSubmitting = useRef(isSubmitting);

    useEffect(() => {
        if (wasSubmitting.current && !isSubmitting && errorCount === 0) setJustSaved(true);
        wasSubmitting.current = isSubmitting;
    }, [isSubmitting, errorCount]);

    useEffect(() => {
        if (isDirty) setJustSaved(false);
    }, [isDirty]);

    return justSaved && !isDirty;
};

const SaveStatus = ({
    isDirty,
    isSubmitting = false,
    errorCount = 0,
    isDisabled = false,
    onSave,
    className,
    children,
    ...props
}: SaveStatusProps) => {
    const { t } = useTranslation('core', { keyPrefix: 'components.saveStatus' });
    const justSaved = useJustSaved(isDirty, isSubmitting, errorCount);

    const status = isSubmitting ? (
        <>
            <SpinnerIcon spin className="text-content-brand-default" />
            {t('saving')}
        </>
    ) : errorCount > 0 ? (
        <>
            <Dot className="bg-surface-danger-strong" />
            {t('errors', { count: errorCount })}
        </>
    ) : isDirty ? (
        <>
            <InfoIcon className="text-content-strong" />
            {t('unsaved')}
        </>
    ) : justSaved ? (
        <>
            <Dot className="bg-surface-success-strong" />
            {t('saved')}
        </>
    ) : null;

    return (
        <div {...props} className={classnames('flex items-center justify-end gap-4', className)}>
            <span role="status" className="flex items-center gap-2 text-sm text-content-strong">
                {status}
            </span>
            {children}
            <Button
                type={onSave ? 'button' : 'submit'}
                variant="primary"
                onClick={onSave}
                disabled={isSubmitting || errorCount > 0 || isDisabled}
            >
                {t('save')}
            </Button>
        </div>
    );
};

export default SaveStatus;
